import React, { useState } from "react";
import {
  CImage,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import ReadMore from "@/components/ReadMore";
import ModerationStatusBadge from "@/components/contentModeration/ModerationStatusBadge";
import avatar from "@/assets/images/avatars/DefaultImg.png";
import { contentModeration as tr } from "@/constants/translation/contentModeration.translation";
import { contentModeration as moderationRoutes } from "@/libs/routes";
import { setFormatDate, dateTimeFormat } from "@/utils/dateFormate";

const NO_VALUE = "-";

const useFallbackImage = () => (e) => {
  e.target.onerror = null;
  e.target.src = avatar;
};

/**
 * Image thumbnail. With `previewable` (and a real image) a click opens the full image in a popup.
 */
const Thumbnail = ({ src, size = 40, previewable = false, title = "" }) => {
  const [open, setOpen] = useState(false);
  const onError = useFallbackImage();
  const canPreview = previewable && Boolean(src);

  return (
    <>
      <CImage
        src={src || avatar}
        rounded
        width={size}
        height={size}
        className={
          canPreview
            ? "moderation-thumb moderation-thumb-clickable"
            : "moderation-thumb"
        }
        style={{ objectFit: "cover", flexShrink: 0 }}
        onError={onError}
        onClick={canPreview ? () => setOpen(true) : undefined}
      />
      {canPreview && (
        <CModal
          visible={open}
          onClose={() => setOpen(false)}
          alignment="center"
          size="lg"
          className="moderation-image-modal"
        >
          <CModalHeader>
            <CModalTitle>{title || " "}</CModalTitle>
          </CModalHeader>
          <CModalBody className="text-center">
            <img
              src={src}
              alt={title}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                borderRadius: 8,
                objectFit: "contain",
              }}
              onError={onError}
            />
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const CreatorCell = ({ creator }) =>
  creator ? (
    <div className="d-flex align-items-center gap-2">
      <Thumbnail src={creator.profilePhotoUrl} size={32} />
      <div>
        <div>{creator.fullName || NO_VALUE}</div>
        {creator.userName && (
          <small className="text-muted">@{creator.userName}</small>
        )}
      </div>
    </div>
  ) : (
    NO_VALUE
  );

const typeLabel = (t, type) =>
  type && tr.type[type] ? t(tr.type[type]) : NO_VALUE;

const formatLocation = (location) => {
  if (!location) return NO_VALUE;
  const text =
    location.address ||
    [
      location.street &&
        `${location.street} ${location.houseNumber || ""}`.trim(),
      location.zipCode,
      location.city,
    ]
      .filter(Boolean)
      .join(", ");
  const tail = [location.region, location.country].filter(Boolean).join(", ");
  return [text, tail].filter(Boolean).join(" - ") || NO_VALUE;
};

const nameColumn = (t, { id, accessorKey, header, image }) => ({
  id,
  accessorKey,
  header: t(header),
  cell: ({ row }) => (
    <div className="d-flex align-items-center gap-2">
      <Thumbnail
        src={row.original[image]}
        previewable
        title={row.original[accessorKey]}
      />
      <ReadMore maxLength={40}>{row.original[accessorKey]}</ReadMore>
    </div>
  ),
});

const createdAtColumn = (t) => ({
  id: "createdAt",
  accessorKey: "createdAt",
  header: t(tr.list.columns.created_at),
  cell: ({ getValue }) => <span>{setFormatDate(getValue()) || NO_VALUE}</span>,
});

const creatorColumn = (t) => ({
  id: "creator",
  accessorKey: "creator",
  header: t(tr.list.columns.creator),
  cell: ({ row }) => <CreatorCell creator={row.original.creator} />,
});

const textColumn = (
  t,
  { id, accessorKey, header, format = (value) => value },
) => ({
  id,
  accessorKey,
  header: t(header),
  cell: ({ row }) => (
    <span>{format(row.original[accessorKey]) ?? NO_VALUE}</span>
  ),
});

const statusColumn = (t) => ({
  id: "moderationStatus",
  accessorKey: "moderationStatus",
  header: t(tr.list.columns.status),
  cell: ({ row }) => (
    <ModerationStatusBadge status={row.original.moderationStatus} />
  ),
});

/**
 * Per-section description of the Content Moderation list and view pages. The list and the view page are generic
 * components; everything that differs between Groups, Communities, Activities and Feed Posts is declared here.
 *
 * - `columns(t)`: the list columns except Actions (added by the list page).
 * - `fields(data, t)`: the rows of the view page's "Details" tab (basic information only).
 */
export const SECTIONS = {
  groups: {
    key: "groups",
    text: tr.sections.groups,
    routes: moderationRoutes.groups,
    getTitle: (data) => data.name,
    getImage: (data) => data.imageUrl,
    columns: (t) => [
      createdAtColumn(t),
      nameColumn(t, {
        id: "name",
        accessorKey: "name",
        header: tr.list.columns.name,
        image: "imageUrl",
      }),
      creatorColumn(t),
      {
        id: "type",
        accessorKey: "type",
        header: t(tr.list.columns.type),
        cell: ({ row }) => <span>{typeLabel(t, row.original.type)}</span>,
      },
      textColumn(t, {
        id: "memberCount",
        accessorKey: "memberCount",
        header: tr.list.columns.members,
      }),
      statusColumn(t),
    ],
    fields: (data, t) => [
      { label: tr.view.fields.name, value: data.name },
      { label: tr.view.fields.type, value: typeLabel(t, data.type) },
      { label: tr.view.fields.members, value: data.memberCount },
      {
        label: tr.view.fields.created_at,
        value: dateTimeFormat(data.createdAt),
      },
      {
        label: tr.view.fields.description,
        value: data.description,
        full: true,
      },
    ],
  },
  communities: {
    key: "communities",
    text: tr.sections.communities,
    routes: moderationRoutes.communities,
    getTitle: (data) => data.name,
    getImage: (data) => data.imageUrl,
    columns: (t) => [
      createdAtColumn(t),
      nameColumn(t, {
        id: "name",
        accessorKey: "name",
        header: tr.list.columns.name,
        image: "imageUrl",
      }),
      textColumn(t, {
        id: "category",
        accessorKey: "category",
        header: tr.list.columns.category,
      }),
      creatorColumn(t),
      textColumn(t, {
        id: "memberCount",
        accessorKey: "memberCount",
        header: tr.list.columns.members,
      }),
      statusColumn(t),
    ],
    fields: (data, t) => [
      { label: tr.view.fields.name, value: data.name },
      { label: tr.view.fields.category, value: data.category },
      { label: tr.view.fields.type, value: typeLabel(t, data.type) },
      { label: tr.view.fields.members, value: data.memberCount },
      {
        label: tr.view.fields.created_at,
        value: dateTimeFormat(data.createdAt),
      },
      {
        label: tr.view.fields.location,
        value: formatLocation(data.location),
        full: true,
      },
      {
        label: tr.view.fields.description,
        value: data.description,
        full: true,
      },
    ],
  },
  activities: {
    key: "activities",
    text: tr.sections.activities,
    routes: moderationRoutes.activities,
    getTitle: (data) => data.title,
    getImage: (data) => data.coverImageUrl,
    columns: (t) => [
      createdAtColumn(t),
      nameColumn(t, {
        id: "title",
        accessorKey: "title",
        header: tr.list.columns.title,
        image: "coverImageUrl",
      }),
      textColumn(t, {
        id: "category",
        accessorKey: "category",
        header: tr.list.columns.category,
      }),
      creatorColumn(t),
      textColumn(t, {
        id: "activityDate",
        accessorKey: "activityDate",
        header: tr.list.columns.activity_date,
        format: (value) => setFormatDate(value),
      }),
      textColumn(t, {
        id: "joinedCount",
        accessorKey: "joinedCount",
        header: tr.list.columns.joined,
      }),
      statusColumn(t),
    ],
    fields: (data) => [
      { label: tr.view.fields.title, value: data.title },
      { label: tr.view.fields.category, value: data.category },
      {
        label: tr.view.fields.activity_date,
        value: setFormatDate(data.activityDate),
      },
      {
        label: tr.view.fields.time,
        value:
          data.startTime && data.endTime
            ? `${data.startTime} - ${data.endTime}`
            : data.startTime,
      },
      { label: tr.view.fields.joined, value: data.joinedCount },
      { label: tr.view.fields.max_participants, value: data.maxParticipants },
      { label: tr.view.fields.join_type, value: data.joinType },
      {
        label: tr.view.fields.created_at,
        value: dateTimeFormat(data.createdAt),
      },
      {
        label: tr.view.fields.location,
        value: formatLocation(data.location),
        full: true,
      },
      {
        label: tr.view.fields.description,
        value: data.description,
        full: true,
      },
    ],
  },
  feedPosts: {
    key: "feedPosts",
    text: tr.sections.feedPosts,
    routes: moderationRoutes.feedPosts,
    getTitle: (data) =>
      (data.caption || data.captionPreview || "").slice(0, 60),
    getImage: (data) => data.imageUrl,
    columns: (t) => [
      createdAtColumn(t),
      {
        id: "post",
        accessorKey: "captionPreview",
        header: t(tr.list.columns.post),
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            {row.original.imageUrl && (
              <Thumbnail
                src={row.original.imageUrl}
                previewable
                title={row.original.caption ?? row.original.captionPreview}
              />
            )}
            <ReadMore maxLength={60}>
              {row.original.caption ?? row.original.captionPreview}
            </ReadMore>
          </div>
        ),
      },
      creatorColumn(t),
      statusColumn(t),
    ],
    fields: (data) => [
      {
        label: tr.view.fields.created_at,
        value: dateTimeFormat(data.createdAt),
      },
      { label: tr.view.fields.text, value: data.caption, full: true },
    ],
  },
};

export { Thumbnail, CreatorCell, NO_VALUE };
